import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import camelize from 'camelize';
import snakeize from 'snakeize';
import urlJoin from 'url-join';
import { urls } from 'app-constants';
import { formatFlexDate } from 'utils';
import BreakText from 'components/common/BreakText';
import Icon from 'components/common/Icon';
import TruncateText from 'components/common/TruncateText';
import { DeleteOverlay } from 'components/common/inlines';
import ItemThumbnail from './ItemThumbnail';

const Item = ({
  counter,
  active,
  selectable,
  selected,
  error,
  onClick,
  onDeleteClick,
  id,
  parent,
  year,
  month,
  day,
  time,
  dateDisplay,
  headline,
  linkUrl,
  description,
  layout,
  mediaSource,
  media,
  mediaUrl,
  mediaDescription,
  mediaCredit,
  mediaDescriptionInherit,
  mediaCreditInherit,
  mediaLinkUrl,
  mediaBackgroundVideo,
  mediaLoop,
  location,
  locationInherit,
  structuredContent,
  tags,
  shouldDelete,
  prefix,
  readOnly = false,
}) => {
  const [mediaObject, setMediaObject] = useState(null);

  useEffect(() => {
    if (media) fetchData(media);
  }, [media]);

  const handleDeleteClick = evt => {
    evt.stopPropagation();
    onDeleteClick();
  };

  const fetchData = mediaId => {
    const url = urlJoin(urls.mediaDataBase, mediaId || '', '/', '?format=json');
    fetch(url, { credentials: 'include' })
      .then(response => {
        if (!response.ok) throw new Error(response.statusText);
        return response;
      })
      .then(response => response.json())
      .then(data => camelize(data))
      .then(data => setMediaObject(data))
      .catch(err => {
        console.error(err);
      });
  };

  const scValue = JSON.stringify(snakeize(structuredContent || []));

  const formFields = !readOnly && (
    <>
      {!!id && <input type="hidden" name={`${prefix}-id`} value={id} />}
      {shouldDelete && <input type="hidden" name={`${prefix}-DELETE`} value="1" />}
      <input type="hidden" name={`${prefix}-parent`} value={parent || ''} />
      <input type="hidden" name={`${prefix}-year`} value={year || ''} />
      <input type="hidden" name={`${prefix}-month`} value={month || ''} />
      <input type="hidden" name={`${prefix}-day`} value={day || ''} />
      <input type="hidden" name={`${prefix}-time`} value={time || ''} />
      <input type="hidden" name={`${prefix}-date_display`} value={dateDisplay || ''} />
      <input type="hidden" name={`${prefix}-headline`} value={headline || ''} />
      <input type="hidden" name={`${prefix}-link_url`} value={linkUrl || ''} />
      <input type="hidden" name={`${prefix}-description`} value={description || ''} />
      <input type="hidden" name={`${prefix}-layout`} value={layout || ''} />
      <input type="hidden" name={`${prefix}-media_source`} value={mediaSource || ''} />
      <input type="hidden" name={`${prefix}-media`} value={media || ''} />
      <input type="hidden" name={`${prefix}-media_url`} value={mediaUrl || ''} />
      <input type="hidden" name={`${prefix}-media_description`} value={mediaDescription || ''} />
      <input type="hidden" name={`${prefix}-media_credit`} value={mediaCredit || ''} />
      <input type="hidden" name={`${prefix}-media_description_inherit`} value={mediaDescriptionInherit} />
      <input type="hidden" name={`${prefix}-media_credit_inherit`} value={mediaCreditInherit} />
      <input type="hidden" name={`${prefix}-media_link_url`} value={mediaLinkUrl || ''} />
      <input type="hidden" name={`${prefix}-media_background_video`} value={mediaBackgroundVideo} />
      <input type="hidden" name={`${prefix}-media_loop`} value={mediaLoop} />
      <input type="hidden" name={`${prefix}-location`} value={location || ''} />
      <input type="hidden" name={`${prefix}-location_inherit`} value={locationInherit} />
      <input type="hidden" name={`${prefix}-structured_content`} value={scValue} />
      <select multiple readOnly name={`${prefix}-tags`} value={tags} style={{ display: 'none' }}>
        {tags.map(v => <option key={v} value={v} />)}
      </select>
    </>
  );

  let formattedDate = '';
  try {
    formattedDate = formatFlexDate({ year, month, day, time });
  } catch (e) {
    console.error(e);
  }

  const containerClasses = classNames({
    active,
    selectable,
    selected,
    error,
    'title-builder-item': true,
    'to-delete': shouldDelete,
  });

  return (
    <div className={containerClasses} onClick={onClick}>
      <div className="index-counter">{counter}</div>

      {selectable && (
        <label className="form-checkbox" style={{ pointerEvents: 'none', paddingRight: 0 }}>
          <input type="checkbox" checked={selected} readOnly />
          <i className="form-icon" />
        </label>
      )}

      <ItemThumbnail
        mediaSource={mediaSource}
        media={media}
        mediaUrl={mediaUrl}
        layout={layout}
        mediaObject={mediaObject}
      />

      <div className="title-builder-item-details">
        <div className="date">{dateDisplay || formattedDate}</div>
        <h6 className="mb-0"><TruncateText text={headline} numChars={75} /></h6>

        {!!media && (
          <div className="meta mt-1 lh-1">
            Media:&nbsp;
            {!!mediaObject && (
              <a href={`${urls.mediaBase}${mediaObject.id}/`} target="_blank" rel="noopener noreferrer">{mediaObject.id}</a>
            )}
          </div>
        )}

        {!!mediaUrl && (
          <div className="meta lh-1">
            Media: <a href={mediaUrl} target="_blank" rel="noopener noreferrer"><BreakText text={mediaUrl} /></a>
          </div>
        )}
      </div>

      {formFields}

      {!readOnly && (
        <>
          {shouldDelete && <DeleteOverlay onUndoClick={handleDeleteClick} />}

          <span
            className="btn-delete tooltip tooltip-left"
            key={shouldDelete}
            onClick={handleDeleteClick}
            data-tooltip={shouldDelete ? 'Undo Remove' : 'Remove Slide'}
          >
            {shouldDelete ? <Icon name="undo" /> : <Icon name="delete" fill />}
          </span>
        </>
      )}
    </div>
  );
};

Item.propTypes = {
  counter: PropTypes.number,
  active: PropTypes.bool,
  selectable: PropTypes.bool,
  selected: PropTypes.bool,
  error: PropTypes.bool,
  onClick: PropTypes.func,
  onDeleteClick: PropTypes.func,
  headline: PropTypes.string,
  linkUrl: PropTypes.string,
  dateDisplay: PropTypes.string,
  id: PropTypes.string,
  parent: PropTypes.string,
  year: PropTypes.number,
  month: PropTypes.number,
  day: PropTypes.number,
  time: PropTypes.string,
  description: PropTypes.string,
  layout: PropTypes.string,
  mediaSource: PropTypes.string,
  media: PropTypes.string,
  mediaUrl: PropTypes.string,
  shouldDelete: PropTypes.bool,
  prefix: PropTypes.string,
  readOnly: PropTypes.bool,
  mediaDescription: PropTypes.string,
  mediaCredit: PropTypes.string,
  mediaDescriptionInherit: PropTypes.bool,
  mediaCreditInherit: PropTypes.bool,
  mediaLinkUrl: PropTypes.string,
  mediaBackgroundVideo: PropTypes.bool,
  mediaLoop: PropTypes.bool,
  location: PropTypes.string,
  locationInherit: PropTypes.bool,
  structuredContent: PropTypes.arrayOf(PropTypes.object),
  tags: PropTypes.array,
};

export default Item;
